export const ROAST_PUBLIC_BASE = 'https://bigdevsoon.me/roast-my-project';

export const ROAST_CATEGORIES = [
  'the_vibe',
  'design_ui',
  'copy_messaging',
  'trust_factor',
  'idea_value_prop',
  'ux_flow',
  'mobile_performance',
  'x_factor',
] as const;

export type RoastCategory = (typeof ROAST_CATEGORIES)[number];

export const CATEGORY_LABELS: Record<RoastCategory, string> = {
  the_vibe: 'The Vibe',
  design_ui: 'Design & UI',
  copy_messaging: 'Copy & Messaging',
  trust_factor: 'Trust Factor',
  idea_value_prop: 'Idea & Value Prop',
  ux_flow: 'UX & Flow',
  mobile_performance: 'Mobile & Perf',
  x_factor: 'The X-Factor',
};

export const MAX_SCORE_PER_CATEGORY = 20;

export interface CategoryScore {
  score: number;
  roast: string;
}

export interface RoastScores {
  the_vibe?: CategoryScore;
  design_ui?: CategoryScore;
  copy_messaging?: CategoryScore;
  trust_factor?: CategoryScore;
  idea_value_prop?: CategoryScore;
  ux_flow?: CategoryScore;
  mobile_performance?: CategoryScore;
  x_factor?: CategoryScore;
  overall: number;
  applicable_categories: number;
}

export interface RoastData {
  id: string;
  slug: string;
  url: string;
  user_id: string | null;
  screenshot_url: string | null;
  status: string;
  reveal_date: string;
  site_name: string | null;
  site_description: string | null;
  site_logo_url: string | null;
  detected_tech: string[];
  category: string;
  pricing_model: string;
  tags: string[];
  socials: Record<string, string>;
  contact_email: string | null;
  scores: RoastScores;
  roast_text: string;
  summary: string | null;
  upvote_count: number;
  comment_count: number;
  submitted_at: string;
  updated_at?: string;
  is_promoted?: boolean;
}

export interface RoastComment {
  id: string;
  roast_id: string;
  user_id: string;
  content: string;
  created_at: string;
  screen_name?: string;
  avatar_url?: string;
}

export const getOverallScoreColor = (score: number): string => {
  if (score <= 25) return 'red.400';
  if (score <= 50) return 'orange.400';
  if (score <= 75) return 'yellow.400';
  return 'green.400';
};

export const getCategoryScoreColor = (score: number): string => {
  if (score <= 5) return 'red.500';
  if (score <= 10) return 'orange.500';
  if (score <= 14) return 'yellow.500';
  if (score <= 17) return 'green.400';
  return 'green.300';
};

export const getCategoryScoreEmoji = (score: number): string => {
  if (score <= 5) return ' 💀';
  return '';
};

export const getStatusBadge = (status: string): { label: string; color: string; bg: string } => {
  switch (status) {
    case 'processing':
      return { label: 'Processing', color: 'blue.400', bg: 'blue.900' };
    case 'ready':
      return { label: 'Live', color: 'green.400', bg: 'green.900' };
    case 'rejected':
      return { label: 'Rejected', color: 'red.400', bg: 'red.900' };
    default:
      return { label: status, color: 'gray.400', bg: 'gray.700' };
  }
};

export const getRoastDisplayName = (roast: { site_name: string | null; url: string; slug: string }): string => {
  if (roast.site_name) return roast.site_name;
  try {
    return new URL(roast.url).hostname.replace('www.', '');
  } catch {
    return roast.slug;
  }
};

export const SITE_CATEGORIES = [
  'saas',
  'tool',
  'portfolio',
  'blog',
  'ecommerce',
  'agency',
  'startup',
  'open-source',
  'community',
  'education',
  'entertainment',
  'finance',
  'health',
  'news',
  'social',
  'other',
] as const;

export const PRICING_MODELS = [
  'free',
  'free-trial',
  'freemium',
  'paid',
  'subscription',
  'one-time',
  'open-source',
  'unknown',
] as const;

export const CATEGORY_DISPLAY_LABELS: Record<string, string> = {
  saas: 'SaaS',
  tool: 'Tool',
  portfolio: 'Portfolio',
  blog: 'Blog',
  ecommerce: 'E-Commerce',
  agency: 'Agency',
  startup: 'Startup',
  'open-source': 'Open Source',
  community: 'Community',
  education: 'Education',
  entertainment: 'Entertainment',
  finance: 'Finance',
  health: 'Health',
  news: 'News',
  social: 'Social',
  other: 'Other',
};

export const PRICING_DISPLAY_LABELS: Record<string, string> = {
  free: 'Free',
  'free-trial': 'Free Trial',
  freemium: 'Freemium',
  paid: 'Paid',
  subscription: 'Subscription',
  'one-time': 'One-time',
  'open-source': 'Open Source',
  unknown: 'Unknown',
};
